import './TransferList.scss'
import {
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
  type MouseEvent
} from 'react'
import {
  MdOutlineCheckBox,
  MdOutlineCheckBoxOutlineBlank,
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
  MdOutlineKeyboardDoubleArrowLeft,
  MdOutlineKeyboardDoubleArrowRight
} from 'react-icons/md'
import { clx } from '../clx'
import { IconButton } from '../IconButton'
import { Spinner } from '../Spinner'
import { useWindow } from '../virtual'
import {
  applyClick,
  checkAll,
  EMPTY_SELECTION,
  extendTo,
  type ClickMode,
  type PaneSelection
} from './selection'

export interface TransferItem {
  id: string
  label: string
}

export interface TransferListProps extends Omit<
  ComponentProps<'div'>,
  'onChange' | 'children' | 'defaultValue'
> {
  /** Every candidate. Duplicate ids are dropped (first wins). */
  items: readonly TransferItem[]
  /** Selected ids, in order. Duplicates are dropped. */
  value: readonly string[]
  onChange: (ids: string[]) => void
  availableLabel?: string
  selectedLabel?: string
  /** Read-only (the portal's show mode): plain rows, no checks, value never changes. */
  isDisabled?: boolean
  isLoading?: boolean
  loadingText?: string
}

/** Row block size in px. Fixed, because the lists virtualise (useWindow). */
const ROW_HEIGHT = 44

/**
 * Dual list for picking a subset of a long list (the portal's
 * TenantTransferInput). Each side is a multiselectable listbox with click,
 * Shift-click range, Ctrl/Cmd-click and keyboard selection, and a filter.
 */
export function TransferList({
  items,
  value,
  onChange,
  availableLabel = 'Available',
  selectedLabel = 'Selected',
  isDisabled = false,
  isLoading = false,
  loadingText = 'Loading...',
  className,
  ref,
  ...rest
}: TransferListProps) {
  const byId = useMemo(() => {
    const map = new Map<string, TransferItem>()
    for (const item of items) if (!map.has(item.id)) map.set(item.id, item)
    return map
  }, [items])

  // Deduped value, unknown ids kept so a change never drops them silently.
  const selectedIds = useMemo(() => [...new Set(value)], [value])

  const { available, selected } = useMemo(() => {
    const valueSet = new Set(selectedIds)
    const left: TransferItem[] = []
    for (const item of byId.values()) if (!valueSet.has(item.id)) left.push(item)
    const right: TransferItem[] = []
    for (const id of selectedIds) {
      const item = byId.get(id)
      if (item) right.push(item)
    }
    return { available: left, selected: right }
  }, [byId, selectedIds])

  const [left, setLeft] = useState<PaneSelection>(EMPTY_SELECTION)
  const [right, setRight] = useState<PaneSelection>(EMPTY_SELECTION)
  const [leftFilter, setLeftFilter] = useState('')
  const [rightFilter, setRightFilter] = useState('')

  const leftVisible = useFiltered(available, leftFilter)
  const rightVisible = useFiltered(selected, rightFilter)

  const checkedIn = (rows: readonly TransferItem[], selection: PaneSelection) =>
    rows.filter((item) => selection.checked.has(item.id)).map((item) => item.id)

  const leftChecked = checkedIn(leftVisible, left)
  const rightChecked = checkedIn(rightVisible, right)

  const add = (ids: string[]) => {
    if (isDisabled || ids.length === 0) return
    onChange([...selectedIds, ...ids])
    setLeft(EMPTY_SELECTION)
  }
  const remove = (ids: string[]) => {
    if (isDisabled || ids.length === 0) return
    const drop = new Set(ids)
    onChange(selectedIds.filter((id) => !drop.has(id)))
    setRight(EMPTY_SELECTION)
  }

  const base = `transfer-list-${useId()}`

  return (
    <div
      ref={ref}
      role="group"
      {...rest}
      aria-busy={isLoading || undefined}
      aria-disabled={isDisabled || undefined}
      className={clx('transfer-list', isDisabled && 'transfer-list--is-disabled', className)}
    >
      {isLoading ? (
        <p className="transfer-list__loading" role="status">
          <Spinner size="sm" />
          {loadingText}
        </p>
      ) : (
        <>
          <Pane
            id={`${base}-available`}
            label={availableLabel}
            total={available.length}
            rows={leftVisible}
            filter={leftFilter}
            onFilter={setLeftFilter}
            selection={left}
            onSelection={setLeft}
            isDisabled={isDisabled}
          />
          <div className="transfer-list__actions">
            <IconButton
              variant="secondary"
              className="transfer-list__move"
              label={`Move selected to ${selectedLabel}`}
              icon={<MdOutlineChevronRight />}
              disabled={isDisabled || leftChecked.length === 0}
              onClick={() => {
                add(leftChecked)
              }}
            />
            <IconButton
              variant="secondary"
              className="transfer-list__move"
              label={`Remove selected from ${selectedLabel}`}
              icon={<MdOutlineChevronLeft />}
              disabled={isDisabled || rightChecked.length === 0}
              onClick={() => {
                remove(rightChecked)
              }}
            />
            <IconButton
              variant="secondary"
              className="transfer-list__move"
              label={`Move all to ${selectedLabel}`}
              icon={<MdOutlineKeyboardDoubleArrowRight />}
              disabled={isDisabled || leftVisible.length === 0}
              onClick={() => {
                add(leftVisible.map((item) => item.id))
              }}
            />
            <IconButton
              variant="secondary"
              className="transfer-list__move"
              label={`Remove all from ${selectedLabel}`}
              icon={<MdOutlineKeyboardDoubleArrowLeft />}
              disabled={isDisabled || rightVisible.length === 0}
              onClick={() => {
                remove(rightVisible.map((item) => item.id))
              }}
            />
          </div>
          <Pane
            id={`${base}-selected`}
            label={selectedLabel}
            total={selected.length}
            rows={rightVisible}
            filter={rightFilter}
            onFilter={setRightFilter}
            selection={right}
            onSelection={setRight}
            isDisabled={isDisabled}
          />
        </>
      )}
    </div>
  )
}

/** Case-insensitive substring filter; lower-cased labels are computed once per list. */
function useFiltered(rows: readonly TransferItem[], filter: string): readonly TransferItem[] {
  const lowered = useMemo(() => rows.map((item) => item.label.toLowerCase()), [rows])
  return useMemo(() => {
    const query = filter.trim().toLowerCase()
    if (!query) return rows
    return rows.filter((_, i) => lowered[i]?.includes(query))
  }, [rows, lowered, filter])
}

interface PaneProps {
  id: string
  label: string
  total: number
  rows: readonly TransferItem[]
  filter: string
  onFilter: (filter: string) => void
  selection: PaneSelection
  onSelection: (selection: PaneSelection) => void
  isDisabled: boolean
}

function Pane({
  id,
  label,
  total,
  rows,
  filter,
  onFilter,
  selection,
  onSelection,
  isDisabled
}: PaneProps) {
  const listRef = useRef<HTMLUListElement>(null)
  const [active, setActive] = useState<string | null>(null)
  const win = useWindow(listRef, { count: rows.length, rowHeight: ROW_HEIGHT })

  const ids = useMemo(() => rows.map((item) => item.id), [rows])
  const index = useMemo(() => new Map(ids.map((rowId, i) => [rowId, i])), [ids])

  const activeIndex = active === null ? -1 : (index.get(active) ?? -1)
  const optionId = (i: number) => `${id}-option-${i}`
  const labelId = `${id}-label`
  const countId = `${id}-count`

  const moveTo = (i: number) => {
    const next = ids[i]
    if (next === undefined) return
    setActive(next)
    win.scrollToIndex(i)
  }

  const onClick = (event: MouseEvent, rowId: string) => {
    const mode: ClickMode = event.shiftKey
      ? 'range'
      : event.ctrlKey || event.metaKey
        ? 'toggle'
        : 'single'
    onSelection(applyClick(selection, ids, index, rowId, mode))
    setActive(rowId)
    listRef.current?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (ids.length === 0) return
    const current = activeIndex < 0 ? 0 : activeIndex
    const last = ids.length - 1
    const step = (to: number) => {
      event.preventDefault()
      const target = Math.max(0, Math.min(last, to))
      if (event.shiftKey) {
        const anchorIndex = selection.anchor === null ? undefined : index.get(selection.anchor)
        const from = anchorIndex ?? (activeIndex < 0 ? target : current)
        onSelection(extendTo({ ...selection, anchor: ids[from] ?? null }, ids, from, target))
      }
      moveTo(target)
    }
    switch (event.key) {
      case 'ArrowDown':
        step(activeIndex < 0 ? 0 : current + 1)
        return
      case 'ArrowUp':
        step(activeIndex < 0 ? 0 : current - 1)
        return
      case 'Home':
        step(0)
        return
      case 'End':
        step(last)
        return
      case ' ': {
        event.preventDefault()
        const rowId = ids[current]
        if (rowId === undefined) return
        onSelection(applyClick(selection, ids, index, rowId, event.shiftKey ? 'range' : 'single'))
        setActive(rowId)
        return
      }
      default:
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
          event.preventDefault()
          onSelection(checkAll(selection, ids))
        }
    }
  }

  const visibleRows = rows.slice(win.start, win.end)

  return (
    <section className="transfer-list__pane">
      <div className="transfer-list__header">
        <span id={labelId} className="transfer-list__label">
          {label}
        </span>
        <span id={countId} className="transfer-list__count">
          {total}
        </span>
      </div>
      <input
        type="search"
        className="transfer-list__filter"
        aria-label={`Filter ${label.toLowerCase()}`}
        aria-controls={`${id}-list`}
        placeholder="Filter"
        value={filter}
        onChange={(event) => {
          onFilter(event.target.value)
        }}
      />
      {isDisabled ? (
        <ul
          id={`${id}-list`}
          ref={listRef}
          role="list"
          tabIndex={0}
          aria-labelledby={`${labelId} ${countId}`}
          className="transfer-list__list"
        >
          {win.before > 0 && <li aria-hidden="true" style={{ blockSize: win.before }} />}
          {visibleRows.map((item) => (
            <li key={item.id} className="transfer-list__row" style={{ blockSize: ROW_HEIGHT }}>
              <span className="transfer-list__text">{item.label}</span>
            </li>
          ))}
          {win.after > 0 && <li aria-hidden="true" style={{ blockSize: win.after }} />}
        </ul>
      ) : (
        <ul
          id={`${id}-list`}
          ref={listRef}
          role="listbox"
          tabIndex={0}
          aria-multiselectable="true"
          aria-labelledby={`${labelId} ${countId}`}
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          className="transfer-list__list"
          onKeyDown={onKeyDown}
        >
          {win.before > 0 && (
            <li role="presentation" aria-hidden="true" style={{ blockSize: win.before }} />
          )}
          {visibleRows.map((item, offset) => {
            const i = win.start + offset
            const isChecked = selection.checked.has(item.id)
            return (
              <li
                key={item.id}
                id={optionId(i)}
                role="option"
                aria-selected={isChecked}
                aria-posinset={i + 1}
                aria-setsize={rows.length}
                data-index={i}
                data-active={i === activeIndex || undefined}
                className="transfer-list__row transfer-list__option"
                style={{ blockSize: ROW_HEIGHT }}
                onMouseDown={(event) => {
                  // Keep Shift-click from selecting text; focus goes to the listbox.
                  event.preventDefault()
                }}
                onClick={(event) => {
                  onClick(event, item.id)
                }}
              >
                <span className="transfer-list__check" aria-hidden="true">
                  {isChecked ? <MdOutlineCheckBox /> : <MdOutlineCheckBoxOutlineBlank />}
                </span>
                <span className="transfer-list__text">{item.label}</span>
              </li>
            )
          })}
          {win.after > 0 && (
            <li role="presentation" aria-hidden="true" style={{ blockSize: win.after }} />
          )}
        </ul>
      )}
    </section>
  )
}
